import { useMemo, useState, useEffect } from "react";
import { DateTime } from "luxon";
import type { EventOccurrence, EventAttendee } from "@/api/clubsApi";
import {
  EventType,
  RsvpState,
  useFetchUserProfileQuery,
  useGetClubQuery,
  useCreateEventReactionMutation,
  useDeleteEventReactionMutation,
} from "@/api/clubsApi";
import { Icon } from "@/components/ui/Icon";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/DropdownMenu";
import { Link } from "@tanstack/react-router";
import { cn } from "@/components/ui/cn";
import { formatHangTitle, lowercaseMeridiem, userDisplayName } from "@tinyclub/shared/utils";
import { EventReactionButton } from "./EventReactionButton";

export interface EventCardHost {
  userId: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
}

interface EventCardProps {
  event: EventOccurrence;
  currentUserId?: string;
  className?: string;
  onClick?: () => void;
  hosts?: EventCardHost[];
  isHosting?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Show club badge + name in host row. Defaults to true. Set false inside club pages. */
  showClubInfo?: boolean;
}

export function EventCard({
  event,
  currentUserId,
  className,
  onClick,
  hosts,
  isHosting,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  showClubInfo = true,
}: EventCardProps) {
  const rootEvent = event.rootEvent;
  const isHang = rootEvent?.event_type === EventType.hang;

  if (isHang) {
    return (
      <HangCard
        event={event}
        currentUserId={currentUserId}
        className={className}
        onClick={onClick}
        hosts={hosts}
        canEdit={canEdit}
        canDelete={canDelete}
        onEdit={onEdit}
        onDelete={onDelete}
        showClubInfo={showClubInfo}
      />
    );
  }

  return (
    <NonHangEventCard
      event={event}
      currentUserId={currentUserId}
      className={className}
      onClick={onClick}
      hosts={hosts}
      isHosting={isHosting}
      canEdit={canEdit}
      canDelete={canDelete}
      onEdit={onEdit}
      onDelete={onDelete}
      showClubInfo={showClubInfo}
    />
  );
}

// ---------------------------------------------------------------------------
// Event Card
// ---------------------------------------------------------------------------

function NonHangEventCard({
  event,
  currentUserId,
  className,
  onClick,
  hosts,
  isHosting,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  showClubInfo,
}: EventCardProps) {
  const rootEvent = event.rootEvent;
  const showOverflowMenu = canEdit || canDelete;

  const { data: club } = useGetClubQuery(rootEvent?.club_id ?? "", {
    skip: !rootEvent?.club_id || !showClubInfo,
  });

  const eventTimezone = rootEvent?.timezone ?? undefined;
  const startDateTime = event.occurrence_start
    ? DateTime.fromISO(event.occurrence_start, { zone: eventTimezone })
    : DateTime.now();

  const isCurrentYear = startDateTime.year === DateTime.now().year;
  const dateLabel = rootEvent?.is_all_day
    ? startDateTime.toFormat(isCurrentYear ? "EEE, MMM d" : "EEE, MMM d, yyyy")
    : lowercaseMeridiem(startDateTime.toFormat(isCurrentYear ? "EEE, MMM d '@' h:mma" : "EEE, MMM d, yyyy '@' h:mma"));

  const locationDisplay = getLocationDisplay(rootEvent);

  const eventImageUrl =
    rootEvent?.primary_image_medium ||
    rootEvent?.primary_image_full ||
    rootEvent?.primary_image_small;

  // RSVP status
  const attendees = event.event_attendees || [];
  const userAttendee = currentUserId
    ? attendees.find((a: EventAttendee) => a.user_id === currentUserId)
    : undefined;
  const userRsvpStatus = userAttendee?.rsvp_status;

  // Past event check — use end time if available, otherwise start time
  const isPast = useMemo(() => {
    const now = DateTime.now();
    if (event.occurrence_end) {
      return DateTime.fromISO(event.occurrence_end) < now;
    }
    return DateTime.fromISO(event.occurrence_start) < now;
  }, [event.occurrence_end, event.occurrence_start]);

  const hasRsvp = userRsvpStatus && userRsvpStatus !== RsvpState.none;
  // For past events, only show the pill if the user actually RSVP'd
  const showRsvpPill = isPast ? hasRsvp : (isHosting || hasRsvp);
  const showBottomRow = showRsvpPill || (!isPast && showOverflowMenu);

  return (
    <div
      onClick={onClick}
      className={cn(
        "rounded-[24px] border border-border-tertiary bg-background p-4",
        "transition-all hover:border-border-secondary hover:shadow-sm",
        onClick && "cursor-pointer",
        className
      )}
    >
      <div className="flex justify-between gap-4">
        {/* Left content */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          {/* Title + host group */}
          <div className="flex flex-col gap-2">
            {/* Title */}
            <h3 className="text-h5 text-text-primary line-clamp-2">
              {rootEvent?.title || "Untitled Event"}
            </h3>

            {/* Host row */}
            {hosts && hosts.length > 0 && (
              <div className="flex items-center gap-[6px] h-5">
                <div className="flex items-center">
                  {club ? (
                    <>
                      {/* Single creator avatar */}
                      <div className="w-5 h-5 rounded-full overflow-hidden border-2 border-background bg-surface-2 flex-shrink-0 z-[2]"
                        style={{ marginRight: -6 }}
                      >
                        {hosts[0].avatarUrl ? (
                          <img
                            src={hosts[0].avatarUrl}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[8px] text-text-tertiary font-medium">
                            {hosts[0].firstName?.[0] || "?"}
                          </div>
                        )}
                      </div>
                      {/* Club badge */}
                      <div
                        className="w-5 h-5 rounded-[5px] overflow-hidden flex-shrink-0 z-[1] flex items-center justify-center"
                        style={{ backgroundColor: club.theme || "#6366f1" }}
                      >
                        {club.primary_image_small || club.primary_image_medium ? (
                          <img
                            src={club.primary_image_small || club.primary_image_medium}
                            alt={club.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-[8px] font-medium text-white">
                            {club.name?.[0]?.toUpperCase() || "C"}
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    hosts.slice(0, 3).map((host, i) => (
                      <div
                        key={host.userId}
                        className="w-5 h-5 rounded-full overflow-hidden border-2 border-background bg-surface-2 flex-shrink-0"
                        style={{ marginRight: i < Math.min(hosts.length, 3) - 1 ? -6 : 0 }}
                      >
                        {host.avatarUrl ? (
                          <img
                            src={host.avatarUrl}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[8px] text-text-tertiary font-medium">
                            {host.firstName?.[0] || "?"}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
                <span className="text-body-2 text-text-secondary truncate">
                  {club
                    ? <>{formatHostNames([hosts[0]])} in <Link to="/clubs/$identifier" params={{ identifier: club.id }} onClick={(e) => e.stopPropagation()} className="font-medium hover:underline">{club.name}</Link></>
                    : formatHostNames(hosts)
                  }
                </span>
              </div>
            )}
          </div>

          {/* Divider + date/location + divider */}
          <div className="flex flex-col gap-2">
            <div className="border-t border-border-subtle" />

            {/* Date & location rows */}
            <div className="flex flex-col gap-2 px-2">
              {/* Date row */}
              <div className="flex items-center gap-[6px]">
                <Icon name="calendar" size={16} className="text-text-tertiary flex-shrink-0" />
                <span className="text-body-3-medium text-text-secondary">{dateLabel}</span>
              </div>

              {/* Location row */}
              {locationDisplay && (
                <div className="flex items-center gap-[6px]">
                  <Icon name="location-pin" size={16} className="text-text-tertiary flex-shrink-0" />
                  <span className="text-body-3-medium text-text-secondary truncate">
                    {locationDisplay}
                  </span>
                </div>
              )}
            </div>

            <div className="border-t border-border-subtle" />
          </div>
        </div>

        {/* Right side image */}
        {eventImageUrl && (
          <div className="flex-shrink-0">
            <img
              src={eventImageUrl}
              alt=""
              className="w-[136px] h-[136px] rounded-[8px] border border-border-tertiary object-cover"
            />
          </div>
        )}
      </div>

      {/* Bottom row: RSVP pill + overflow menu (full width, below image) */}
      {showBottomRow && (
        <>
          <div className="mt-3" />
          <div className="flex items-center justify-between">
            {showRsvpPill ? (
              <RsvpStatusPill
                status={isHosting && !isPast ? RsvpState.yes : userRsvpStatus!}
                isHosting={isHosting && !isPast}
              />
            ) : (
              <div />
            )}
            {!isPast && showOverflowMenu && (
              <div className="pr-2">
                <OverflowMenu
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hang Card
// ---------------------------------------------------------------------------

function HangCard({
  event,
  currentUserId,
  className,
  onClick,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  showClubInfo,
}: Omit<EventCardProps, "isHosting">) {
  const rootEvent = event.rootEvent;
  const showOverflowMenu = canEdit || canDelete;

  // Fetch creator profile for hang title generation
  const { data: creatorProfile } = useFetchUserProfileQuery(
    rootEvent?.creator_id ?? "",
    { skip: !rootEvent?.creator_id }
  );

  const { data: club } = useGetClubQuery(rootEvent?.club_id ?? "", {
    skip: !rootEvent?.club_id || !showClubInfo,
  });

  const eventTimezone = rootEvent?.timezone ?? undefined;
  const startDateTime = event.occurrence_start
    ? DateTime.fromISO(event.occurrence_start, { zone: eventTimezone })
    : DateTime.now();
  const endDateTime = event.occurrence_end
    ? DateTime.fromISO(event.occurrence_end, { zone: eventTimezone })
    : null;

  // Auto-generated hang title
  const title = useMemo(() => {
    const customName = rootEvent?.location_name || "";
    const fallbackName =
      rootEvent?.location_object?.properties?.name_preferred ||
      rootEvent?.location_object?.properties?.name ||
      "";
    const displayLocation =
      customName && customName !== fallbackName
        ? `${customName}, ${fallbackName}`
        : customName || fallbackName;
    const address =
      rootEvent?.location_object?.properties?.full_address ||
      rootEvent?.location_object?.properties?.place_formatted ||
      "";
    const isCurrentUser = currentUserId === rootEvent?.creator_id;
    return formatHangTitle(
      creatorProfile ?? null,
      displayLocation,
      address,
      startDateTime,
      endDateTime,
      isCurrentUser
    );
  }, [rootEvent, creatorProfile, currentUserId, startDateTime, endDateTime]);

  // Past event check — use end time if available, otherwise start time
  const isPast = useMemo(() => {
    const now = DateTime.now();
    if (event.occurrence_end) {
      return DateTime.fromISO(event.occurrence_end) < now;
    }
    return DateTime.fromISO(event.occurrence_start) < now;
  }, [event.occurrence_end, event.occurrence_start]);

  // Countdown text
  const [countdownText, setCountdownText] = useState("");

  useEffect(() => {
    const update = () => {
      const now = DateTime.now();
      if (endDateTime && now > endDateTime) {
        const diff = now.diff(endDateTime, ["hours", "minutes"]);
        const h = Math.floor(diff.hours);
        const m = Math.floor(diff.minutes);
        if (h > 0) {
          setCountdownText(`Ended ${h}h ${m}m ago`);
        } else {
          setCountdownText(`Ended ${m}m ago`);
        }
        return;
      }
      if (now >= startDateTime && (!endDateTime || now <= endDateTime)) {
        setCountdownText("Happening now");
        return;
      }
      const diff = startDateTime.diff(now, ["hours", "minutes"]);
      const h = Math.floor(diff.hours);
      const m = Math.floor(diff.minutes);
      if (h > 0) {
        setCountdownText(`Starts in ${h}h ${m}m`);
      } else {
        setCountdownText(`Starts in ${m}m`);
      }
    };

    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [startDateTime, endDateTime]);

  // Reaction state
  const [createReaction] = useCreateEventReactionMutation();
  const [deleteReaction] = useDeleteEventReactionMutation();
  const [optimisticReactionActive, setOptimisticReactionActive] = useState(false);
  const [optimisticReactionId, setOptimisticReactionId] = useState<string | null>(null);

  const userReaction = useMemo(
    () => {
      const reactions = event.event_reactions || [];
      return currentUserId
        ? reactions.find((r: { user_id?: string; id: string }) => r.user_id === currentUserId) ?? null
        : null;
    },
    [event.event_reactions, currentUserId]
  );
  const isReactionActive = !!userReaction || optimisticReactionActive;

  useEffect(() => {
    if (userReaction) {
      setOptimisticReactionActive(false);
      setOptimisticReactionId(userReaction.id);
    } else if (!optimisticReactionActive) {
      setOptimisticReactionId(null);
    }
  }, [userReaction, optimisticReactionActive]);

  const handleActivateReaction = async () => {
    if (!event.id || !currentUserId) return;
    setOptimisticReactionActive(true);
    try {
      const result = await createReaction({
        occurrenceId: event.id,
        reactionType: "custom_handsUp",
      }).unwrap();
      const newReaction = Array.isArray(result) ? result[0] : result;
      setOptimisticReactionId(newReaction.id);
    } catch (error) {
      console.error("Error creating reaction:", error);
      setOptimisticReactionActive(false);
      setOptimisticReactionId(null);
    }
  };

  const handleDeactivateReaction = async () => {
    const reactionId = userReaction?.id || optimisticReactionId;
    if (!reactionId || !event.id) return;
    setOptimisticReactionActive(false);
    try {
      await deleteReaction({ reactionId, occurrenceId: event.id }).unwrap();
    } catch (error) {
      console.error("Error deleting reaction:", error);
      setOptimisticReactionActive(true);
    }
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        "rounded-[24px] border border-border-tertiary bg-background p-4",
        "transition-all hover:border-border-secondary hover:shadow-sm",
        onClick && "cursor-pointer",
        className
      )}
    >
      {/* Top: bolt + creator avatar + club badge | overflow menu */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-yellow flex items-center justify-center flex-shrink-0 z-[2]">
              <Icon name="bolt" size={14} className="text-text-primary" />
            </div>
            {creatorProfile && (
              <div
                className="w-8 h-8 rounded-full overflow-hidden border-2 border-background bg-surface-2 flex-shrink-0 z-[1]"
                style={{ marginLeft: -8 }}
              >
                {creatorProfile.avatar_small || creatorProfile.avatar_medium ? (
                  <img
                    src={creatorProfile.avatar_small || creatorProfile.avatar_medium || ""}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-text-tertiary font-medium">
                    {creatorProfile.first_name?.[0] || "?"}
                  </div>
                )}
              </div>
            )}
            {club && (
              <div
                className="w-8 h-8 rounded-[8px] overflow-hidden flex-shrink-0 z-[0] flex items-center justify-center"
                style={{ marginLeft: -8, backgroundColor: club.theme || "#6366f1" }}
              >
                {club.primary_image_small || club.primary_image_medium ? (
                  <img
                    src={club.primary_image_small || club.primary_image_medium}
                    alt={club.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-[13px] font-medium text-white">
                    {club.name?.[0]?.toUpperCase() || "C"}
                  </span>
                )}
              </div>
            )}
          </div>
          {club && creatorProfile && (
            <span className="text-body-2 text-text-secondary truncate">
              {userDisplayName(creatorProfile)} in <Link to="/clubs/$identifier" params={{ identifier: club.id }} onClick={(e) => e.stopPropagation()} className="font-medium hover:underline">{club.name}</Link>
            </span>
          )}
        </div>
        {showOverflowMenu && (
          <div className="pr-2">
            <OverflowMenu
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          </div>
        )}
      </div>

      {/* Title */}
      <h3 className="text-h4 text-text-primary line-clamp-2 mb-1">
        {title}
      </h3>

      {/* Description */}
      {rootEvent?.description && (
        <p className="text-body-1 text-text-secondary line-clamp-2 mb-3">
          {rootEvent.description}
        </p>
      )}

      {/* Bottom row: countdown + reaction */}
      <div className="flex items-center justify-between mt-3">
        <div className="bg-yellow rounded-[8px] flex gap-2 items-center h-10 p-[6px]">
          <Icon name="clock" size={16} className="text-text-primary" />
          <span className="text-body-3-medium text-text-primary">
            {countdownText}
          </span>
        </div>

        {!isPast && (
          <div onClick={(e) => e.stopPropagation()}>
            <EventReactionButton
              isSelected={isReactionActive}
              onActivate={handleActivateReaction}
              onDeactivate={handleDeactivateReaction}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared components
// ---------------------------------------------------------------------------

function RsvpStatusPill({
  status,
  isHosting,
}: {
  status: RsvpState;
  isHosting?: boolean;
}) {
  if (isHosting) {
    return (
      <span className="inline-flex self-start items-center bg-blue text-white rounded-full px-4 py-1 text-body-3-medium">
        Hosting
      </span>
    );
  }

  const config: Record<string, { label: string; className: string } | undefined> = {
    yes: {
      label: "Going",
      className: "bg-olive text-white px-4 py-1",
    },
    maybe: {
      label: "Maybe",
      className: "bg-surface-2 text-text-primary px-4 py-1",
    },
    no: {
      label: "Not going",
      className: "bg-surface-2 text-text-primary px-4 py-1",
    },
  };

  const pill = config[status];
  if (!pill) return null;

  return (
    <span
      className={`inline-flex self-start items-center rounded-full text-body-3-medium ${pill.className}`}
    >
      {pill.label}
    </span>
  );
}

function OverflowMenu({
  canEdit,
  canDelete,
  onEdit,
  onDelete,
}: {
  canEdit?: boolean;
  canDelete?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="p-1 -mr-1 text-text-tertiary hover:text-text-secondary rounded-lg hover:bg-surface-2 transition-colors"
          aria-label="Event options"
        >
          <Icon name="ellipsis" size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canEdit && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.();
            }}
          >
            <Icon name="pen" size={14} />
            Edit
          </DropdownMenuItem>
        )}
        {canEdit && canDelete && <DropdownMenuSeparator />}
        {canDelete && (
          <DropdownMenuItem
            destructive
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.();
            }}
          >
            <Icon name="trash" size={14} />
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getLocationDisplay(rootEvent: EventOccurrence["rootEvent"]): string {
  if (!rootEvent) return "";
  if (rootEvent.location_name) return rootEvent.location_name;
  return (
    rootEvent.location_object?.properties?.name_preferred ||
    rootEvent.location_object?.properties?.name ||
    ""
  );
}

function formatHostNames(hosts: EventCardHost[]): string {
  if (hosts.length === 0) return "";
  const name = (h: EventCardHost) =>
    h.lastName ? `${h.firstName} ${h.lastName}` : h.firstName;
  if (hosts.length === 1) return name(hosts[0]);
  if (hosts.length === 2) return `${name(hosts[0])} & ${name(hosts[1])}`;
  return `${name(hosts[0])} & ${hosts.length - 1} others`;
}
